import React from 'react';
import { Stack, Box } from '@mui/material';
import { BasePrice } from 'modules/equipment/types';
import { DiscountType } from '@trnsact/trnsact-shared-types';
import { ListingForm } from 'components/shared/TableListing/types';
import { FieldPath, useFormContext, useWatch } from 'react-hook-form';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';
import { CurrencyInputField, RadioGroupField, InterestInputField } from 'components/form';

import { chargeTypeOptions } from '../../../../../constants/form';
import { AttachmentChargeWithTypeValue } from './AttachmentChargeWithTypeValue';
import { QuoteChargeListingEntity, QuoteChargeType } from '../../../../../types';

interface Props {
  rowIndex: number;
  type: keyof BasePrice;
}

export const ChargeValueWithTypeCell = ({ type, rowIndex }: Props) => {
  const { control } = useFormContext<ListingForm<QuoteChargeListingEntity>>();

  const { type: cellType, equipment } = useWatch({ control, name: `bodyFields.${rowIndex}` });

  const fields: Record<
    QuoteChargeType,
    Record<'valuePath' | 'typePath', FieldPath<ListingForm<QuoteChargeListingEntity>>>
  > = {
    [QuoteChargeType.Equipment]: {
      valuePath: `bodyFields.${rowIndex}.equipment.price.${type}`,
      typePath: `bodyFields.${rowIndex}.equipment.price.${type}Type` as any,
    },
    [QuoteChargeType.Invoice]: {
      valuePath: `bodyFields.${rowIndex}.charge.price.${type}`,
      typePath: `bodyFields.${rowIndex}.charge.price.${type}Type` as any,
    },
  };

  const fieldType = useWatch({ control, name: fields[cellType].typePath });

  const fieldMapper: Record<DiscountType, any> = {
    [DiscountType.Percent]: (
      <InterestInputField
        control={control}
        name={fields[cellType].valuePath}
        textFieldProps={{ variant: 'outlined' }}
      />
    ),
    [DiscountType.Flat]: (
      <CurrencyInputField
        allowNegative
        control={control}
        name={fields[cellType].valuePath}
        textFieldProps={{ variant: 'outlined' }}
      />
    ),
  };

  return (
    <Stack width="100%" height="100%" gap="0.5rem" justifyContent="flex-end">
      <Box height="100%">
        {fieldMapper[fieldType as DiscountType]}
        <RadioGroupField row control={control} options={chargeTypeOptions} name={fields[cellType].typePath} />
      </Box>

      <RenderWithCondition condition={!!equipment?.attachments}>
        {equipment?.attachments?.map((_, index) => (
          <AttachmentChargeWithTypeValue key={index} type={type} rowIndex={rowIndex} attachmentIndex={index} />
        ))}
      </RenderWithCondition>
    </Stack>
  );
};
